/**
 * Is the viewport phone-sized?
 *
 * 🔴 WHY A MEDIA QUERY AND NOT A MEASUREMENT. The shell also measures real widths to decide
 * whether the account chip collapses, and that is right for a desktop window being dragged
 * narrower. On an actual phone it was observed NOT to fire: the header clipped the nav toggle
 * out of view before the chip ever gave its width back, leaving an operator on a 412px screen
 * with a sidebar they could not close. A media query cannot fail to measure.
 *
 * Shared, because Chat and the shell must agree on where "phone" starts. Two copies of this
 * number drifting apart would put the Chat page in its phone layout inside a desktop shell,
 * or the reverse.
 */
import { useEffect, useState } from 'react'

/** Below this the UI is a PHONE, not a narrow desktop. Tailwind's `md`. */
export const NARROW_PX = 768

export function useIsNarrow(): boolean {
  const [narrow, setNarrow] = useState(() => matchesNarrow())
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return
    const mq = window.matchMedia(`(max-width: ${NARROW_PX - 1}px)`)
    const onChange = (e: MediaQueryListEvent) => setNarrow(e.matches)
    setNarrow(mq.matches)
    // `addListener` is the deprecated form; kept as a fallback because some embedded webviews
    // still ship it and this must not throw there.
    if (mq.addEventListener) { mq.addEventListener('change', onChange); return () => mq.removeEventListener('change', onChange) }
    mq.addListener(onChange)
    return () => mq.removeListener(onChange)
  }, [])
  return narrow
}

/** The same test the hook seeds from, for a caller that needs it before a render. */
export function matchesNarrow(): boolean {
  return typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia(`(max-width: ${NARROW_PX - 1}px)`).matches
}
